<template>
    <div class="call-settings-tab">
        <div class="tab-header">
            <h3>Arama Ayarları</h3>
            <p>Arama süreçleri ve telesekreter ayarlarını yapılandırın.</p>
        </div>

        <a-form layout="vertical" class="settings-form">
            <!-- Voicemail Detection -->
            <div class="settings-section">
                <div class="setting-header">
                    <div class="setting-title">
                        <h4>Telesekreter Algılama</h4>
                        <div class="setting-description">
                            Telesekreter algılandığında aramayı kapatın veya mesaj bırakın.
                        </div>
                    </div>
                    <a-switch v-model="formData.enable_voicemail_detection" @change="updateSettings" />
                </div>

                <div v-if="formData.enable_voicemail_detection" class="setting-content">
                    <a-form-item label="Telesekreter Mesajı">
                        <a-input
                            v-model="formData.voicemail_message"
                            placeholder="Telesekreter mesajınızı girin"
                            @change="updateSettings"
                        />
                        <div class="description">
                            Telesekreter tespit edildiğinde bırakılacak mesaj. Boş bırakırsanız, telesekreter
                            algılandığında arama sonlandırılır.
                        </div>
                    </a-form-item>

                    <a-form-item label="Algılama Zaman Aşımı">
                        <div class="slider-container">
                            <a-slider
                                v-model="formData.voicemail_detection_timeout_ms"
                                :min="5000"
                                :max="180000"
                                :step="5000"
                                @change="updateSettings"
                                class="slider"
                            />
                            <div class="input-with-unit">
                                <a-input-number
                                    v-model="formData.voicemail_detection_timeout_ms"
                                    :min="5000"
                                    :max="180000"
                                    :step="5000"
                                    @change="updateSettings"
                                    :formatter="value => `${value / 1000}`"
                                    :parser="value => value * 1000"
                                />
                                <span class="unit">saniye</span>
                            </div>
                        </div>
                        <div class="description">
                            Telesekreter algılamasının ne kadar süreyle çalışacağını belirler. Varsayılan 30 saniyedir.
                        </div>
                    </a-form-item>
                </div>
            </div>

            <!-- User Keypad Input Detection (DTMF) -->
            <div class="settings-section">
                <div class="setting-header">
                    <div class="setting-title">
                        <h4>Kullanıcı Tuş Takımı Girdisi (DTMF)</h4>
                        <div class="setting-description">
                            Arama sırasında kullanıcının tuş takımından yaptığı DTMF girişlerini algılayın ve işleyin.
                        </div>
                    </div>
                    <a-switch v-model="formData.allow_user_dtmf" @change="updateSettings" />
                </div>

                <div v-if="formData.allow_user_dtmf" class="setting-content">
                    <div class="description">Aşağıdaki koşullardan herhangi biri gerçekleştiğinde asistan yanıt verir:</div>

                    <!-- Timeout -->
                    <a-form-item label="Zaman Aşımı">
                        <div class="slider-container">
                            <a-slider
                                v-model="formData.user_dtmf_options.timeout_ms"
                                :min="1000"
                                :max="15000"
                                :step="100"
                                @change="updateSettings"
                                class="slider"
                            />
                            <div class="input-with-unit">
                                <a-input-number
                                    v-model="formData.user_dtmf_options.timeout_ms"
                                    :min="1000"
                                    :max="15000"
                                    :step="100"
                                    @change="updateSettings"
                                    :formatter="value => `${(value / 1000).toFixed(1)}`"
                                    :parser="value => Math.round(parseFloat(value) * 1000)"
                                />
                                <span class="unit">saniye</span>
                            </div>
                        </div>
                        <div class="description">Belirtilen süre içinde DTMF girişi yapılmazsa yanıt üretilir. Her tuş ile süre yenilenir.</div>
                    </a-form-item>

                    <!-- Termination Key -->
                    <a-form-item label="Sonlandırma Tuşu">
                        <div class="setting-header" style="justify-content:flex-start; margin-bottom: 8px;">
                            <a-switch v-model="dtmf_enable_termination_key" @change="onToggleTerminationKey" />
                            <div class="setting-description" style="margin-left:10px;">Kullanıcı belirlenen tuşa bastığında giriş tamamlanır.</div>
                        </div>
                        <a-select
                            v-if="dtmf_enable_termination_key"
                            v-model="formData.user_dtmf_options.termination_key"
                            style="width: 160px"
                            @change="updateSettings"
                        >
                            <a-select-option v-for="key in terminationKeyOptions" :key="key" :value="key">{{ key }}</a-select-option>
                        </a-select>
                    </a-form-item>

                    <!-- Digit Limit -->
                    <a-form-item label="Hane Sınırı">
                        <div class="setting-header" style="justify-content:flex-start; margin-bottom: 8px;">
                            <a-switch v-model="dtmf_enable_digit_limit" @change="onToggleDigitLimit" />
                            <div class="setting-description" style="margin-left:10px;">Belirlenen sayıdaki tuşa basıldığında giriş tamamlanır.</div>
                        </div>
                        <a-input-number
                            v-if="dtmf_enable_digit_limit"
                            v-model="formData.user_dtmf_options.digit_limit"
                            :min="1"
                            :max="50"
                            :step="1"
                            @change="updateSettings"
                        />
                    </a-form-item>
                </div>
            </div>

            <!-- End Call on Silence -->
            <div class="settings-section">
                <div class="setting-header">
                    <h4>Sessizlikte Aramayı Sonlandır</h4>
                </div>
                <div class="setting-content">
                    <div class="slider-container">
                        <a-slider
                            v-model="formData.end_call_after_silence_ms"
                            :min="10000"
                            :max="600000"
                            :step="10000"
                            @change="updateSettings"
                            class="slider"
                        />
                        <div class="input-with-unit">
                            <a-input-number 
                                v-model="formData.end_call_after_silence_ms" 
                                :min="10000"
                                :max="600000"
                                :step="10000"
                                @change="updateSettings"
                                :formatter="value => `${value / 1000}`"
                                :parser="value => value * 1000"
                            />
                            <span class="unit">saniye</span>
                        </div>
                    </div>
                    <div class="description">
                        Kullanıcı belirtilen süre boyunca sessiz kalırsa aramayı sonlandırır. Minimum 10 saniye,
                        maksimum 10 dakikadır.
                    </div>
                </div>
            </div>

            <!-- Max Call Duration -->
            <div class="settings-section">
                <div class="setting-header">
                    <h4>Maksimum Çağrı Süresi</h4>
                </div>
                <div class="setting-content">
                    <div class="slider-container">
                        <a-slider
                            v-model="formData.max_call_duration_ms"
                            :min="60000"
                            :max="7200000"
                            :step="60000"
                            @change="updateSettings"
                            class="slider"
                        />
                        <div class="input-with-unit">
                            <a-input-number 
                                v-model="formData.max_call_duration_ms" 
                                :min="60000"
                                :max="7200000"
                                :step="60000"
                                @change="updateSettings"
                                :formatter="value => `${value / 60000}`"
                                :parser="value => value * 60000"
                            />
                            <span class="unit">dakika</span>
                        </div>
                    </div>
                    <div class="description">
                        İzin verilen maksimum çağrı süresi. Bu süre aşıldığında çağrı zorla sonlandırılır. Minimum 1
                        dakika, maksimum 2 saattir.
                    </div>
                </div>
            </div>

            <!-- Pause Before Speaking -->
            <div class="settings-section">
                <div class="setting-header">
                    <h4>Konuşmadan Önce Duraklama</h4>
                    <div class="setting-description">
                        Çağrının başlangıcında asistanın konuşmaya başlamadan önce bekleyeceği süre.
                    </div>
                </div>
                <div class="setting-content">
                    <div class="slider-container">
                        <a-slider
                            v-model="formData.begin_message_delay_ms"
                            :min="0"
                            :max="5000"
                            :step="100"
                            @change="updateSettings"
                            class="slider"
                        />
                        <div class="input-with-unit">
                            <a-input-number 
                                v-model="formData.begin_message_delay_ms"
                                :min="0"
                                :max="5000"
                                :step="100"
                                @change="updateSettings"
                                :formatter="value => `${value / 1000}`"
                                :parser="value => value * 1000"
                            />
                            <span class="unit">saniye</span>
                        </div>
                    </div>
                    <div class="description">
                        Kullanıcının çağrıya hazırlanması için konuşmayı geciktirir. Maksimum 5 saniyedir.
                    </div>
                </div>
            </div>

            <!-- Ring Duration -->
            <div class="settings-section">
                <div class="setting-header">
                    <h4>Çalma Süresi</h4>
                    <div class="setting-description">
                        Telefon cevaplanmadan önce ya da sonlandırılmadan önce çalma süresi.
                    </div>
                </div>
                <div class="setting-content">
                    <div class="slider-container">
                        <a-slider
                            v-model="formData.ring_duration_ms"
                            :min="5000"
                            :max="90000"
                            :step="5000"
                            @change="updateSettings"
                            class="slider"
                        />
                        <div class="input-with-unit">
                            <a-input-number
                                v-model="formData.ring_duration_ms"
                                :min="5000"
                                :max="90000"
                                :step="5000"
                                @change="updateSettings"
                                :formatter="value => `${value / 1000}`"
                                :parser="value => value * 1000"
                            />
                            <span class="unit">saniye</span>
                        </div>
                    </div>
                    <div class="description">
                        Giden aramalarda ve çağrı aktarımında çalma süresini belirler. Varsayılan 30 saniyedir.
                    </div>
                </div>
            </div>

            <!-- Kaydet Butonu -->
            <div class="form-actions">
                <PermissionController :permissions="['voice-agent.update']">
                    <a-button type="primary" @click="saveSettings" :loading="isSaving">
                        Değişiklikleri Kaydet
                    </a-button>
                </PermissionController>
            </div>
        </a-form>
    </div>
</template>

<script>
import _ from "lodash";

export default {
    name: "CallSettingsTab",
    props: {
        agent: {
            type: Object,
            default: () => null,
        },
    },
    data() {
        return {
            formData: {
                enable_voicemail_detection: false,
                voicemail_message: "",
                voicemail_detection_timeout_ms: 30000,
                end_call_after_silence_ms: 600000,
                max_call_duration_ms: 3600000,
                begin_message_delay_ms: 1000,
                ring_duration_ms: 30000,
                allow_user_dtmf: true,
                user_dtmf_options: {
                    digit_limit: null,
                    termination_key: null,
                    timeout_ms: 2500,
                },
            },
            isSaving: false,
            debounceUpdate: null,
            terminationKeyOptions: ["0","1","2","3","4","5","6","7","8","9","#","*"],
            dtmf_enable_digit_limit: false,
            dtmf_enable_termination_key: false,
        };
    },
    watch: {
        agent: {
            handler(newValue) {
                if (newValue) {
                    this.initializeFormData();
                }
            },
            immediate: true,
            deep: true,
        },
    },
    created() {
        // Debounce fonksiyonu oluşturma
        this.debounceUpdate = _.debounce(this.triggerUpdate, 1000);

        if (this.agent) {
            this.initializeFormData();
        }
    },
    methods: {
        initializeFormData() {
            // Agent'dan verileri formData'ya aktar
            this.formData.enable_voicemail_detection =
                this.agent.enable_voicemail_detection !== undefined ? this.agent.enable_voicemail_detection : false;
            this.formData.voicemail_message = this.agent.voicemail_message || "";
            this.formData.voicemail_detection_timeout_ms = this.agent.voicemail_detection_timeout_ms || 30000;
            this.formData.end_call_after_silence_ms = this.agent.end_call_after_silence_ms || 600000;
            this.formData.max_call_duration_ms = this.agent.max_call_duration_ms || 3600000;
            this.formData.begin_message_delay_ms =
                this.agent.begin_message_delay_ms !== undefined ? this.agent.begin_message_delay_ms : 1000;
            this.formData.ring_duration_ms = this.agent.ring_duration_ms || 30000;

            // DTMF
            this.formData.allow_user_dtmf = this.agent.allow_user_dtmf !== undefined ? this.agent.allow_user_dtmf : true;
            var dtmf = this.agent.user_dtmf_options || {};
            this.formData.user_dtmf_options = {
                digit_limit: typeof dtmf.digit_limit === "number" ? dtmf.digit_limit : null,
                termination_key: dtmf.termination_key || null,
                timeout_ms: typeof dtmf.timeout_ms === "number" ? dtmf.timeout_ms : 2500,
            };

            this.dtmf_enable_digit_limit = typeof this.formData.user_dtmf_options.digit_limit === "number";
            this.dtmf_enable_termination_key = !!this.formData.user_dtmf_options.termination_key;
        },
        updateSettings() {
            // Değişiklikleri toplu olarak göndermek için debounce kullan
            this.debounceUpdate();
        },
        triggerUpdate() {
            // Değişiklikleri parent komponente bildir
            // console.log("Değişiklikler:", this.formData);
        },
        saveSettings() {
            this.isSaving = true;

            // Gönderimden önce opsiyonel alanları null'a çek
            if (!this.dtmf_enable_digit_limit) {
                this.formData.user_dtmf_options.digit_limit = null;
            }
            if (!this.dtmf_enable_termination_key) {
                this.formData.user_dtmf_options.termination_key = null;
            }

            // Agent verisini güncelle
            this.$emit("update", this.formData);

            setTimeout(() => {
                this.isSaving = false;
            }, 1000);
        },
        onToggleDigitLimit(checked) {
            if (!checked) {
                this.formData.user_dtmf_options.digit_limit = null;
            } else if (!this.formData.user_dtmf_options.digit_limit) {
                this.formData.user_dtmf_options.digit_limit = 10;
            }
            this.updateSettings();
        },
        onToggleTerminationKey(checked) {
            if (!checked) {
                this.formData.user_dtmf_options.termination_key = null;
            } else if (!this.formData.user_dtmf_options.termination_key) {
                this.formData.user_dtmf_options.termination_key = "#";
            }
            this.updateSettings();
        },
        formatTime(ms) {
            return ms / 1000 + " s";
        },
        formatTimeMinutes(ms) {
            const minutes = Math.floor(ms / 60000);
            const seconds = Math.floor((ms % 60000) / 1000);

            if (minutes === 0) {
                return seconds + " s";
            } else if (seconds === 0) {
                return minutes + " dk";
            } else {
                return minutes + " dk " + seconds + " s";
            }
        },
        formatTimeHours(ms) {
            const hours = Math.floor(ms / 3600000);
            const minutes = Math.floor((ms % 3600000) / 60000);

            if (hours === 0) {
                return minutes + " dk";
            } else if (minutes === 0) {
                return hours + " s";
            } else {
                return hours + " sa " + minutes + " dk";
            }
        },
    },
};
</script>

<style scoped lang="scss">
.call-settings-tab {
    padding: 20px 0;

    .tab-header {
        margin-bottom: 20px;

        h3 {
            margin: 0 0 10px 0;
            font-weight: 500;
        }

        p {
            color: #666;
            margin: 0;
        }
    }

    .settings-form {
        max-width: 800px;
    }

    .settings-section {
        background-color: #f9f9f9;
        padding: 15px;
        border-radius: 4px;
        margin-bottom: 20px;
        border: 1px solid #e8e8e8;

        .setting-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 15px;

            h4 {
                margin: 0 0 5px 0;
                font-weight: 500;
            }

            .setting-title {
                flex: 1;
            }
        }

        .setting-description {
            color: #666;
            font-size: 13px;
            margin-top: 2px;
        }

        .setting-content {
            padding-top: 5px;
        }
    }

    .slider-container {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 5px;

        .slider {
            flex: 1;

            :deep(.ant-slider-rail) {
                background-color: #e1e1e1;
                height: 4px;
            }

            :deep(.ant-slider-track) {
                background-color: #1890ff;
                height: 4px;
            }

            :deep(.ant-slider-handle) {
                border: solid 2px #1890ff;
            }
        }

        .input-with-unit {
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 120px;

            :deep(.ant-input-number) {
                width: 80px;
            }

            .unit {
                color: #666;
                font-size: 13px;
                font-weight: 500;
                white-space: nowrap;
            }
        }

        .slider-value {
            width: 80px;
            text-align: right;
            font-weight: 500;
        }
    }

    .description {
        color: #666;
        font-size: 12px;
        margin-top: 5px;
        margin-bottom: 10px;
    }

    .form-actions {
        margin-top: 20px;
        text-align: right;
    }
}
</style>
